import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { DateTime } from "luxon";

import { TicketType } from "../model/domain/Entity";
import {
  TicketCutoffDefaultType,
  TicketCutoffPermission as TicketCutoffPermission,
  TicketCutoffException,
  TicketCutoffResume,
  TicketCutoffHistory,
  TicketCutoffType,
  TicketCutoffExceptionModel,
  TicketCutoffDefaultModel,
  TicketCutoffDefault,
  SelectedAccountManagerFund,
  TicketCutoffTicketPermission,
} from "../components/cutoff/ticket-cutoff-type.model";
import { environment } from "../../environments/environment";
import { TicketCutoffTypeEnum } from "../enum/cutoff-type.enum";
import { map } from "rxjs/operators";

@Injectable()
export class CutoffService {
  private readonly _warningMessage =
    "Não é possível encaminhar a boleta após horário limite ";

  constructor(private http: HttpClient) {}

  get warningMessage() {
    return this._warningMessage;
  }

  /**
   * @description Check if is available to send
   * @param ticketType
   * @returns if available to send
   */
  async isAvailable(
    ticketType: TicketCutoffTypeEnum,
    ticketId: number,
    accountManagerId: number = null,
    fundId: number = null
  ): Promise<TicketCutoffPermission> {
    const isAllowed = await this.isAllowed(
      ticketType,
      ticketId,
      accountManagerId,
      fundId
    );

    return isAllowed;
  }

  async isAllowed(
    ticketType: TicketCutoffTypeEnum,
    ticketId: number,
    accountManagerId: number = null,
    fundId: number = null
  ): Promise<TicketCutoffPermission> {
    return this.getPermisssion(ticketType, ticketId, accountManagerId, fundId)
      .toPromise()
      .then((result) => {
        return result;
      });
  }

  list(): Observable<Array<TicketCutoffDefaultType>> {
    return this.http.get<Array<TicketCutoffDefaultType>>(
      `${environment.apiUrls.tickets}/cutoff`
    );
  }

  get(cutoffDefaultId: Number): Observable<TicketCutoffDefaultType> {
    return this.http
      .get<TicketCutoffDefaultType>(
        `${environment.apiUrls.tickets}/cutoff/${cutoffDefaultId}`
      )
      .pipe(map((response) => this.transformTicketCutoffType(response)));
  }

  getCutoffDefaultByCutoffTypeAccManagerFund(
    cutoffTypeId: number,
    accountManagerId: number,
    fundId: number
  ): Observable<TicketCutoffDefaultType> {
    const params: any = {};

    if (cutoffTypeId) {
      params.cutoffTypeId = cutoffTypeId;
    }
    if (accountManagerId) {
      params.accountManagerId = accountManagerId;
    }
    if (fundId) {
      params.fundId = fundId;
    }

    return this.http
      .get<TicketCutoffDefaultType>(
        `${environment.apiUrls.tickets}/cutoff/get-cutoff-default-by-cutofftype-accountmanager-fund`,
        { params }
      )
      .pipe(map((response) => this.transformTicketCutoffType(response)));
  }

  getCutoffExceptionByCutoffTypeAccManagerFund(
    cutoffTypeId: number,
    accountManagerId: number,
    fundId: number
  ): Observable<TicketCutoffResume[]> {
    const params: any = {};

    if (cutoffTypeId) {
      params.cutoffTypeId = cutoffTypeId;
    }
    if (accountManagerId) {
      params.accountManagerId = accountManagerId;
    }
    if (fundId) {
      params.fundId = fundId;
    }

    return this.http.get<TicketCutoffResume[]>(
      `${environment.apiUrls.tickets}/cutoff/get-cutoff-exception-by-cutofftype-accountmanager-fund`,
      { params }
    );
  }

  getPermisssion(
    cutoffTypeId: Number,
    ticketId: number,
    accountManagerId: number = null,
    fundId: number = null
  ): Observable<TicketCutoffPermission> {
    const params: any = {};

    if (ticketId) {
      params.ticketId = ticketId;
    }

    if (accountManagerId) {
      params.accountManagerId = accountManagerId;

      if (fundId) {
        params.fundId = fundId;
      }
    }
    return this.http.get<TicketCutoffPermission>(
      `${environment.apiUrls.tickets}/cutoff/${cutoffTypeId}/permission`,
      { params }
    );
  }

  getMultiplePermisssion(
    ticketList: TicketCutoffTicketPermission[]
  ): Observable<TicketCutoffPermission[]> {
    return this.http.post<TicketCutoffPermission[]>(
      `${environment.apiUrls.tickets}/cutoff/get-multiple-permission`,
      ticketList
    );
  }

  private transformTicketCutoffType(
    response: TicketCutoffDefaultType
  ): TicketCutoffDefaultType {
    return response;
  }

  put(
    cutoffId: number,
    model: TicketCutoffDefaultType
  ): Observable<TicketCutoffDefaultType> {
    return this.http.put<TicketCutoffDefaultType>(
      `${environment.apiUrls.tickets}/cutoff/${cutoffId}`,
      model
    );
  }

  post(
    cutoffId: number,
    model: TicketCutoffException
  ): Observable<TicketCutoffException> {
    return this.http.post<TicketCutoffException>(
      `${environment.apiUrls.tickets}/cutoff/${cutoffId}`,
      model
    );
  }

  registerExceptionInBatch(
    model: TicketCutoffExceptionModel
  ): Observable<TicketCutoffException> {
    return this.http.post<TicketCutoffException>(
      `${environment.apiUrls.tickets}/cutoff/register-exception-batch/`,
      model
    );
  }

  registerDefaultInBatch(
    model: TicketCutoffDefaultModel
  ): Observable<TicketCutoffDefault> {
    return this.http.post<TicketCutoffDefault>(
      `${environment.apiUrls.tickets}/cutoff/register-default-batch/`,
      model
    );
  }

  listExceptionHistory(
    cutoffTypeId: number = null,
    accountManagerId: number = null,
    fundId: number = null,
    selectedAccountManagerFundList: SelectedAccountManagerFund[] = null
  ): Observable<Array<TicketCutoffHistory>> {
    const params: any = {};

    if (cutoffTypeId) {
      params.cutoffTypeId = cutoffTypeId;
    }

    if (accountManagerId) {
      params.accountManagerId = accountManagerId;

      if (fundId) {
        params.fundId = fundId;
      }
    }

    return this.http
      .get<Array<TicketCutoffHistory>>(
        `${environment.apiUrls.tickets}/cutoff/${cutoffTypeId}/history-exception`,
        { params }
      )
      .pipe(map((response) => this.transformDefaultHistoryTime(response)));
  }

  listDefaultHistory(cutoffId: number): Observable<Array<TicketCutoffHistory>> {
    return this.http
      .get<Array<TicketCutoffHistory>>(
        `${environment.apiUrls.tickets}/cutoff/${cutoffId}/history-default`
      )
      .pipe(map((response) => this.transformDefaultHistoryTime(response)));
  }

  private transformDefaultHistoryTime(
    response: TicketCutoffHistory[]
  ): TicketCutoffHistory[] {
    return response.map((item) => {
      return item;
    });
  }

  listResume(): Observable<Array<TicketCutoffResume>> {
    return this.http
      .get<Array<TicketCutoffResume>>(
        `${environment.apiUrls.tickets}/cutoff/resume`
      )
      .pipe(
        map((response) => {
          return this.transformResumeTime(response);
        })
      );
  }

  listResumeIndex(): Observable<Array<TicketCutoffResume>> {
    return this.http.get<Array<TicketCutoffResume>>(
      `${environment.apiUrls.tickets}/cutoff/resume-index`
    );
  }

  listResumeByCutoffType(
    cutoffTypeId: number
  ): Observable<TicketCutoffResume[]> {
    return this.http
      .get<TicketCutoffResume[]>(
        `${environment.apiUrls.tickets}/cutoff/${cutoffTypeId}/resume`
      )
      .pipe(
        map((response) => {
          return this.transformResumeTime(response);
        })
      );
  }

  listCutoffType(): Observable<Array<TicketCutoffType>> {
    return this.http.get<Array<TicketCutoffType>>(
      `${environment.apiUrls.tickets}/cutoff/cutoff-type`
    );
  }

  getCutoffType(cutoffTypeId: number): Observable<TicketCutoffType> {
    return this.http.get<TicketCutoffType>(
      `${environment.apiUrls.tickets}/cutoff/${cutoffTypeId}/cutoff-type/`
    );
  }

  getRegisteredAccountManagerFundByCutoffType(
    cutoffTypeId: number
  ): Observable<SelectedAccountManagerFund[]> {
    return this.http.get<SelectedAccountManagerFund[]>(
      `${environment.apiUrls.tickets}/cutoff/${cutoffTypeId}/cutoff-registered-accountmanager-fund/`
    );
  }

  private transformResumeTime(
    response: TicketCutoffResume[]
  ): TicketCutoffResume[] {
    return response.map((item) => {
      return item;
    });
  }

  /**
   * @description Convert time string format into date
   * @param limitTime
   */
  private stringToDate(limitTime: string): Date {
    const utcNow = DateTime.utc();
    const defaultHour = +limitTime.substr(0, 2);
    const defaultMinute = +limitTime.substr(3, 2);
    return utcNow
      .set({ hour: defaultHour, minute: defaultMinute, second: 0 })
      .toJSDate();
  }

  /**
   * @description Convert DateTimeOffset format into date
   * @param limitTime
   */
  public dateTimeOffsetToTimeString(dateTimeLimit: Date): string {
    const defaultHour = dateTimeLimit.toString().substr(11, 2);
    const defaultMinute = dateTimeLimit.toString().substr(14, 2);
    return `${defaultHour}:${defaultMinute}`;
  }

  /**
   * @description Convert time string in time plus offset
   * @param limitTime
   * @returns string with time in format HH:mm with offset added
   */
  timeToString(limitTime: string): string {
    const utcNow = DateTime.local();
    const defaultHour = +limitTime.substr(0, 2);
    const defaultMinute = +limitTime.substr(3, 2);
    const offset = utcNow.offset * -1;
    const date = utcNow
      .set({ hour: defaultHour, minute: defaultMinute, second: 0 })
      .plus({ minutes: offset });
    const result = date.toFormat("HH:mm");
    return result;
  }
}
